(() => {
	"use strict";

	const POPUP_COMPONENT_SELECTOR = "popup-component";
	const POPUP_CLOSE_SELECTOR = "[data-popup-close]";
	const NOTIFICATION_FORM_SELECTOR = "[data-notification-form]";
	const NOTIFICATION_HEADING_SELECTOR = "[data-product-notification-heading]";
	const NOTIFICATION_MESSAGE_SELECTOR = ".product__notification__message";
	const FORM_WRAPPER_SELECTOR = "[data-form-wrapper]";
	const BUTTONS_BLOCK_SELECTOR = ".product__block--buttons";
	const INLINE_SUCCESS_SELECTOR = "[data-product-notification-success]";
	const SUCCESS_CLASS = "has-success";
	const HIDDEN_CLASS = "hidden";
	const STORAGE_KEY = "notification_form_id";

	class ProductNotification extends HTMLElement {
		constructor() {
			super();

			this.notificationForm = this.querySelector(NOTIFICATION_FORM_SELECTOR);
			this.preventSubmit = true;
			this.popup = this.closest("dialog");
			this.popupClose = this.popup
				? this.popup.querySelector(POPUP_CLOSE_SELECTOR)
				: null;
		}

		connectedCallback() {
			if (!this.notificationForm || !this.popup) return;

			this.checkState();
			this.notificationForm.addEventListener("submit", (event) =>
				this.notificationSubmitEvent(event),
			);
			this.popupClose?.addEventListener("click", () => {
				this.removeStorage();
			});
		}

		checkState() {
			const isContactPosted =
				window.location.search.indexOf("contact_posted=true") !== -1;
			const storedSubmission = this.getStoredSubmission();
			const isSameProductPath =
				storedSubmission &&
				(!storedSubmission.path ||
					storedSubmission.path === window.location.pathname);
			const formWrapper = this.closest(FORM_WRAPPER_SELECTOR);
			const isSoldOutState =
				formWrapper && formWrapper.classList.contains("variant--soldout");

			if (!(isContactPosted && isSameProductPath)) {
				return;
			}

			if (isSoldOutState) {
				const notificationHeading = this.querySelector(
					NOTIFICATION_HEADING_SELECTOR,
				);
				notificationHeading?.classList.add(HIDDEN_CLASS);

				const popupComponent = this.closest(POPUP_COMPONENT_SELECTOR);
				popupComponent?.classList.add(SUCCESS_CLASS);

				this.popup.removeAttribute("inert");
				if (typeof this.popup.showModal === "function") {
					this.popup.showModal();
				} else {
					this.popup.setAttribute("open", "");
				}

				const popupSuccessMessage =
					this.querySelector(NOTIFICATION_MESSAGE_SELECTOR) || this.popup;
				this.scrollToSuccessMessage(popupSuccessMessage);
			} else {
				const inlineSuccessMessage = this.showInlineSuccess(formWrapper);
				this.scrollToSuccessMessage(inlineSuccessMessage);
				this.closePopup();
			}

			this.removeStorage();
			this.removeContactPostedParam();
		}

		notificationSubmitEvent(event) {
			if (!this.preventSubmit) return;

			event.preventDefault();
			this.removeStorage();
			this.writeStorage();
			this.preventSubmit = false;
			this.notificationForm.submit();
		}

		getStoredSubmission() {
			if (typeof window.sessionStorage === "undefined") {
				return null;
			}

			const storedValue = window.sessionStorage.getItem(STORAGE_KEY);
			if (!storedValue) return null;

			try {
				const parsedValue = JSON.parse(storedValue);
				if (
					parsedValue &&
					typeof parsedValue === "object" &&
					typeof parsedValue.path === "string"
				) {
					return parsedValue;
				}
			} catch (_) {
				// Backward compatibility with older string value
			}

			return {
				path: window.location.pathname,
				formId: storedValue,
			};
		}

		writeStorage() {
			if (typeof window.sessionStorage === "undefined") return;

			window.sessionStorage.setItem(
				STORAGE_KEY,
				JSON.stringify({
					formId: this.notificationForm.id,
					path: window.location.pathname,
				}),
			);
		}

		removeStorage() {
			if (typeof window.sessionStorage === "undefined") return;

			window.sessionStorage.removeItem(STORAGE_KEY);
		}

		closePopup() {
			if (!this.popup) return;

			if (this.popup.hasAttribute("open")) {
				if (typeof this.popup.close === "function") {
					this.popup.close();
				} else {
					this.popup.removeAttribute("open");
				}
			}

			this.popup.setAttribute("inert", "");
		}

		showInlineSuccess(formWrapper) {
			if (!formWrapper) return null;

			const messageText = this.dataset.successMessage?.trim();
			if (!messageText) return null;

			let successMessage = formWrapper.querySelector(INLINE_SUCCESS_SELECTOR);
			if (!successMessage) {
				successMessage = document.createElement("p");
				successMessage.className = "form-success product-notification__success";
				successMessage.setAttribute("data-product-notification-success", "");

				const buttonsBlock = formWrapper.querySelector(BUTTONS_BLOCK_SELECTOR);
				if (buttonsBlock) {
					buttonsBlock.insertAdjacentElement("afterend", successMessage);
				} else {
					formWrapper.appendChild(successMessage);
				}
			}

			successMessage.textContent = messageText;
			return successMessage;
		}

		scrollToSuccessMessage(element) {
			if (!element) return;

			const prefersReducedMotion =
				typeof window.matchMedia === "function" &&
				window.matchMedia("(prefers-reduced-motion: reduce)").matches;
			const behavior = prefersReducedMotion ? "auto" : "smooth";

			requestAnimationFrame(() => {
				if (this.popup?.hasAttribute("open") && this.popup.contains(element)) {
					element.scrollIntoView({ behavior, block: "nearest" });
					return;
				}

				if (typeof window.theme?.scrollTo === "function") {
					window.theme.scrollTo(element.getBoundingClientRect().top);
					return;
				}

				element.scrollIntoView({ behavior, block: "center" });
			});
		}

		removeContactPostedParam() {
			const url = new URL(window.location.href);
			if (!url.searchParams.has("contact_posted")) return;

			url.searchParams.delete("contact_posted");
			const nextSearch = url.searchParams.toString();
			const nextUrl = `${url.pathname}${nextSearch ? `?${nextSearch}` : ""}${url.hash}`;
			window.history.replaceState({}, document.title, nextUrl);
		}
	}

	if (!customElements.get("product-notification")) {
		customElements.define("product-notification", ProductNotification);
	}
})();
